import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import SEO from "@/components/site/SEO";
import { useLanguage } from "@/i18n/LanguageContext";
import { resolveMediaUrl } from "@/lib/api";
import { useCachedGet } from "@/hooks/useCachedGet";
import PageSkeleton from "@/components/site/PageSkeleton";

const formatDate = (iso, locale) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale || "id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Career = () => {
  const { dict, locale } = useLanguage();
  const copy = dict.career;
  const { data, ready } = useCachedGet("/careers", { published: true });
  const items = Array.isArray(data) ? data : [];
  const [preview, setPreview] = useState(null);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = formatDate(item.posted_at || item.created_at, locale);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return Array.from(map.entries());
  }, [items, locale]);

  if (!ready) return <PageSkeleton />;

  return (
    <div>
      <SEO title={copy.seoTitle} description={copy.seoDesc} />
      <section className="pt-12 pb-8 bg-[#fff5f5]">
        <div className="container-wide max-w-4xl">
          <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{copy.overline}</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-black text-zinc-900 tracking-tighter">{copy.title}</h1>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed">{copy.lead}</p>
        </div>
      </section>

      <section className="pb-16 md:pb-24 bg-[#fff5f5]">
        <div className="container-wide">
          {grouped.length === 0 && (
            <div className="bg-white rounded-3xl border border-zinc-100 p-12 text-center text-zinc-500">
              {copy.empty}
            </div>
          )}

          <div className="space-y-12">
            {grouped.map(([dateLabel, posts]) => (
              <div key={dateLabel}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-px flex-1 bg-maroon-100" />
                  <div className="px-4 py-1.5 rounded-full bg-white border border-maroon-100 text-sm font-semibold text-maroon-800">
                    {dateLabel}
                  </div>
                  <div className="h-px flex-1 bg-maroon-100" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPreview(p)}
                      className="text-left group bg-white rounded-3xl overflow-hidden border border-zinc-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition"
                    >
                      <div className="aspect-[3/4] bg-zinc-100 overflow-hidden">
                        <img
                          src={resolveMediaUrl(p.poster)}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-display font-bold text-lg text-zinc-900 group-hover:text-maroon-800 transition">
                          {p.title}
                        </h3>
                        {p.description && (
                          <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{p.description}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-3xl w-full max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-white text-zinc-800 flex items-center justify-center shadow"
            >
              <X size={18} />
            </button>
            <img
              src={resolveMediaUrl(preview.poster)}
              alt={preview.title}
              className="w-full max-h-[92vh] object-contain rounded-2xl bg-white"
            />
            <div className="mt-3 text-center text-white">
              <div className="font-semibold text-lg">{preview.title}</div>
              <div className="text-sm text-white/70">{formatDate(preview.posted_at || preview.created_at, locale)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;
